// src/features/stock/pages/StockListPage.js
import React, { useState } from 'react';
import { Plus, Download, Upload, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useStock } from '../hooks/useStock';
import { useStockMovement } from '../hooks/useStockMovement';
import StockTable from '../components/StockTable';
import StockFilters from '../components/StockFilters';
import StockStatistics from '../components/StockStatistics';
import StockForm from '../components/StockForm';
import StockMovementForm from '../components/StockMovementForm';
import StockBulkActions from '../components/StockBulkActions';
import Button from '../../../shared/components/Button';
import Card from '../../../shared/components/Card';
import Spinner from '../../../shared/components/Spinner';

const StockListPage = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [showMovementForm, setShowMovementForm] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedForMovement, setSelectedForMovement] = useState(null);
  

   // 3. Custom hooks execute
  const stockData = useStock();  // This triggers data fetching
  const { createMovement } = useStockMovement();


  const {
    items,
    loading,
    pagination,
    filters,
    updateFilters,
    changePage,
    createItem,
    updateItem,
    deleteItem,
    bulkDelete,
  } = stockData;

  //console.log('useStock hook returns:', useStock());



  const handleAddItem = () => {
    setSelectedItem(null);
    setShowForm(true);
  };

  const handleEditItem = (item) => {
    setSelectedItem(item);
    setShowForm(true);
  };

  const handleViewItem = (item) => {
    navigate(`/stock/${item.id}`);
  };

  const handleDeleteItem = async (item) => {
    if (window.confirm(`Are you sure you want to delete ${item.name}?`)) {
      await deleteItem(item.id);
    }
  };

  const handleMovement = (item) => {
    setSelectedForMovement(item);
    setShowMovementForm(true);
  };

  const handleFormSubmit = async (data) => {
    if (selectedItem) {
      await updateItem(selectedItem.id, data);
    } else {
      await createItem(data);
    }
  };

  const handleMovementSubmit = async (data) => {
    await createMovement({
      ...data,
      item_id: selectedForMovement.id,
    });
    setShowMovementForm(false);
    setSelectedForMovement(null);
  };

  const handleSelectAll = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(item => item.id));
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems(prev =>
      prev.includes(id)
        ? prev.filter(itemId => itemId !== id)
        : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`Are you sure you want to delete ${selectedItems.length} items?`)) {
      await bulkDelete(selectedItems);
      setSelectedItems([]);
    }
  };

  const handleExport = async () => {
    // Implement export functionality
  };

  const handleImport = () => {
    // Implement import functionality
  };

  const stats = {
    totalItems: 1250,
    totalValue: 456789,
    lowStockItems: 23,
    expiringSoon: 12,
    itemChange: '+12%',
    valueChange: '+8%',
    lowStockChange: '-5%',
    expiringChange: '+3%',
  };

  if (loading && items.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory Management</h1>
          <p className="text-gray-600 mt-1">Manage your medical supplies and equipment</p>
        </div>
        <div className="flex space-x-3">
          {/* <Button
            variant="secondary"
            icon={Upload}
            onClick={handleImport}
          >
            Import
          </Button>
          <Button
            variant="secondary"
            icon={Download}
            onClick={handleExport}
          >
            Export
          </Button>
          <Button
            variant="primary"
            icon={Plus}
            onClick={handleAddItem}
          >
            Add Item
          </Button> */}
        </div>
      </div>

      {/* Statistics */}
      {/* <StockStatistics stats={stats} /> */}

      {/* Filters */}
      <StockFilters
        filters={filters}
        onFilterChange={updateFilters}
        onClearFilters={() => updateFilters({})}
      />

      {/* Bulk Actions */}
      {selectedItems.length > 0 && (
        <StockBulkActions
          selectedCount={selectedItems.length}
          onDelete={handleBulkDelete}
          onClear={() => setSelectedItems([])}
        />
      )}

      {/* Stock Table */}
      <StockTable
        items={items}
        onEdit={handleEditItem}
        onDelete={handleDeleteItem}
        onView={handleViewItem}
        onMovement={handleMovement}
        selectedItems={selectedItems}
        onSelectItem={handleSelectItem}
        onSelectAll={handleSelectAll}
      />

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between bg-white px-4 py-3 rounded-lg shadow-sm">
          <div className="text-sm text-gray-700">
            Showing page {pagination.currentPage} of {pagination.totalPages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => changePage(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => changePage(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Forms */}
      <StockForm
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setSelectedItem(null);
        }}
        onSubmit={handleFormSubmit}
        initialData={selectedItem}
      />

      <StockMovementForm
        isOpen={showMovementForm}
        onClose={() => {
          setShowMovementForm(false);
          setSelectedForMovement(null);
        }}
        onSubmit={handleMovementSubmit}
        item={selectedForMovement}
      />
    </div>
  );
};

export default StockListPage;